'use client';

import { CircleCheck, Inbox, Radio, Wifi, WifiOff } from 'lucide-react';
import { useTranslations } from 'next-intl';

import { Link } from '@/i18n/routing';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';

import type { ActiveOcrJob, OcrProgressStage } from '../types';
import { OcrProgressBar } from './ocr-progress-bar';

interface ActiveOcrJobsDropdownProps {
  jobs: ActiveOcrJob[];
  fallbackToPolling: boolean;
}

const STAGE_KEY: Record<OcrProgressStage, string> = {
  preparing: 'progressStage.preparing',
  uploading: 'progressStage.uploading',
  extracting: 'progressStage.extracting',
  parsing: 'progressStage.parsing',
  storing: 'progressStage.storing'
};

export function ActiveOcrJobsDropdown({
  jobs,
  fallbackToPolling
}: ActiveOcrJobsDropdownProps) {
  const t = useTranslations('ocr');

  return (
    <div className='flex flex-col'>
      <div className='flex items-center justify-between border-b px-3 py-2'>
        <div className='flex items-center gap-2'>
          <Radio className='text-primary h-3.5 w-3.5' />
          <span className='text-sm font-medium'>{t('activeJobs.title')}</span>
        </div>
        <span className='text-muted-foreground text-[10px] font-medium'>
          {jobs.length}
        </span>
      </div>

      {jobs.length === 0 ? (
        <div className='flex flex-col items-center gap-2 px-4 py-8 text-center'>
          <CircleCheck className='h-7 w-7 text-emerald-500/60' />
          <p className='text-muted-foreground text-sm'>
            {t('activeJobs.empty')}
          </p>
        </div>
      ) : (
        <ScrollArea className='max-h-[320px]'>
          <ul className='divide-y'>
            {jobs.map((job) => (
              <li key={job.id}>
                {job.container_id ? (
                  <Link
                    href={`/dashboard/ocr-containers/${job.container_id}`}
                    className='hover:bg-muted/50 flex flex-col gap-1.5 px-3 py-2.5 transition-colors'
                  >
                    <ActiveJobRow job={job} stageLabel={t} />
                  </Link>
                ) : (
                  <div className='flex flex-col gap-1.5 px-3 py-2.5'>
                    <ActiveJobRow job={job} stageLabel={t} />
                  </div>
                )}
              </li>
            ))}
          </ul>
        </ScrollArea>
      )}

      <div className='bg-muted/30 flex items-center justify-between border-t px-3 py-1.5'>
        <div
          className={cn(
            'flex items-center gap-1.5 text-[10px] font-medium',
            fallbackToPolling
              ? 'text-amber-600 dark:text-amber-500'
              : 'text-emerald-600 dark:text-emerald-500'
          )}
        >
          {fallbackToPolling ? (
            <WifiOff className='h-3 w-3' />
          ) : (
            <Wifi className='h-3 w-3' />
          )}
          <span>
            {fallbackToPolling ? t('activeJobs.polling') : t('activeJobs.live')}
          </span>
        </div>
        <Link
          href='/dashboard/ocr-containers'
          className='text-primary text-[10px] font-medium hover:underline'
        >
          {t('activeJobs.viewAll')}
        </Link>
      </div>
    </div>
  );
}

function ActiveJobRow({
  job,
  stageLabel
}: {
  job: ActiveOcrJob;
  stageLabel: (key: string) => string;
}) {
  return (
    <>
      <div className='flex items-center justify-between gap-2'>
        <span className='truncate text-sm font-medium'>
          {job.container_label ?? <Inbox className='h-3.5 w-3.5' />}
        </span>
        <span className='text-muted-foreground text-[10px] font-medium tracking-wider uppercase'>
          {job.progress_stage
            ? stageLabel(STAGE_KEY[job.progress_stage])
            : `${job.progress_percent}%`}
        </span>
      </div>
      <OcrProgressBar
        currentPage={job.current_page}
        totalPages={job.total_pages}
        stage={job.progress_stage}
        compact
      />
    </>
  );
}
